import {
  buildMapCollisionWorld,
  getHideoutModule,
  isGeneratorRunning,
  mintFactoryMods,
  GYM_MODULE_ID,
  ITEM_CARBINE,
  RANGE_MODULE_ID,
  type CollisionWorld,
  type HideoutState,
  type ItemInstance,
  type ProfileWire,
  type RaidEnv,
  type Vec3,
} from "@rietz/shared";
import type { Object3D } from "three";
import type { DebugTools } from "../../debug/tools.js";
import type { Input } from "../../input/input.js";
import type { Prediction } from "../../net/prediction.js";
import { buildHideoutProps } from "../../render/hideoutProps.js";
import { projectHideout } from "../../ui/meta/hideoutLogic.js";
import { setHideoutGym, setHideoutStation, setInteractPrompt } from "../../ui/store.js";
import { distanceToBox, rayHitsBox, viewDirection } from "../interact.js";
import type { WorldView } from "../world.js";
import {
  buildHideoutLayout,
  hideoutLayoutKey,
  STATION_REACH_M,
  type HideoutLayout,
  type HideoutStationSlot,
} from "./layout.js";
import type { RangeControl, RangeWeapon } from "./range.js";
import { pickRangeWeapon } from "./rangeLogic.js";

/**
 * The walkable hideout: the cellar as a place rather than a tab. The player
 * walks a client-generated map (`layout.ts`); F on a station opens the panel
 * the tab used to draw; builds, crafts, fuel and power are the same
 * `UiActions` verbs. It is walked with the raid's own machinery (`Prediction`,
 * `stepMovement`, the collision world, the viewmodel) with the socket muted
 * (`GameLoop.localOnly`) — a second movement implementation would drift.
 * Nothing is sent, so nothing is authorised.
 *
 * `setEnv({ time: "night" })` before the map loads is about the ambient floor:
 * a shelter has no sky, and only its bulbs should light it.
 *
 * A build finishing or the generator switching changes what the shelter looks
 * like while the player stands in it; `update` compares the layout key (and
 * generator flag) and rebuilds the whole map. The rebuild does NOT teleport:
 * a module only ever goes from rubble to built, so what appears is furniture
 * inside an existing room and what disappears is rubble — falling a few
 * centimetres onto a floor is a correct outcome.
 */

/** The shelter's ambient floor, not a claim about the hour. */
const SHELTER_ENV: RaidEnv = { time: "night", weather: "clear" };

/**
 * How often the projected hideout is re-derived without a new profile — for the
 * one change nobody pushes, the generator running dry.
 */
const RECHECK_INTERVAL_MS = 1000;

/**
 * How far outside `firingBox` the feet may stray before the gun goes away — a
 * shooter shifting their weight is not leaving.
 */
const RANGE_LEAVE_SLACK_M = 0.5;

export interface HideoutSessionOptions {
  view: WorldView;
  input: Input;
  prediction: Prediction;
  /** The firing point. Constructed by `main.ts`, because the bullet pool has to
   *  be able to report its impacts to it. */
  range: RangeControl;
  /** The F3–F6 layers, so collision boxes can be looked at in here too. */
  tools?: DebugTools;
  /** The believed profile, read fresh every frame — see `update`. */
  profile: () => ProfileWire | null;
  /** `performance.now()` at which that profile landed (`HudState.profileAtMs`). */
  profileAtMs: () => number;
  /**
   * A station panel or the gym overlay opened or closed. The session is the
   * only writer, so it tells `main.ts` to re-arbitrate the pointer.
   */
  onOverlayChanged: (open: boolean) => void;
  /**
   * The collision world changed (entering, or a rebuild) — `main.ts` repoints
   * its one `collision` variable.
   */
  onWorldChanged: (world: CollisionWorld) => void;
}

export class HideoutSession {
  private readonly o: HideoutSessionOptions;

  private on = false;

  private layout: HideoutLayout | null = null;
  private world: CollisionWorld | null = null;
  private key = "";
  private generatorRunning = false;
  /** Range level 2: the timed drill is equipment, not a difficulty setting. */
  private drillAvailable = false;

  /**
   * One prompt per station, built when the layout is installed — it cannot
   * change without a rebuild.
   */
  private prompts: readonly string[] = [];

  /** The profile the layout was last derived from, by REFERENCE. A new profile
   *  object is the cheap signal that something in the cellar may have moved. */
  private lastProfile: ProfileWire | null = null;
  private nextCheckAtMs = 0;

  /** The two overlays, mirrored — this class is the only writer of both. */
  private stationOpen: string | null = null;
  private gymOpen = false;

  /**
   * The plate glows the props builder handed over. Belongs to the meshes; a
   * rebuild replaces it, and it is handed to the range.
   */
  private propsGlows: ReadonlyMap<string, Object3D> = new Map();

  /** Mints instance ids for the loaner carbine. Local and never sent, so the
   *  namespace only has to be unique within this session. */
  private iidSeq = 0;

  /** Scratch — the station scan runs every frame and must not allocate. */
  private readonly eye: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly dir: Vec3 = { x: 0, y: 0, z: 0 };

  constructor(opts: HideoutSessionOptions) {
    this.o = opts;
  }

  get active(): boolean {
    return this.on;
  }

  /** What the shelter is made of, or `null` while nobody is down there. */
  get collision(): CollisionWorld | null {
    return this.world;
  }

  /**
   * Down the stairs. `false` = nothing to walk into (no profile yet); the
   * caller stays in the menu.
   */
  enter(): boolean {
    const profile = this.o.profile();
    if (profile === null) return false;

    const h = this.project(profile);
    this.lastProfile = profile;
    this.nextCheckAtMs = performance.now() + RECHECK_INTERVAL_MS;
    this.install(h, true);
    this.on = true;
    setInteractPrompt(null);
    return true;
  }

  /**
   * Back up the stairs. The collision world is not rebuilt here — `main.ts`
   * owns that and puts it back to the void.
   */
  leave(): void {
    if (!this.on) return;
    this.o.range.stop();
    this.closeOverlay();
    // Also disposes the props group through the disposer `install` attached.
    this.o.view.clearMap();
    this.o.tools?.setMap(null);
    setInteractPrompt(null);
    this.on = false;
    this.layout = null;
    this.world = null;
    this.key = "";
    this.prompts = [];
    this.lastProfile = null;
  }

  /**
   * Escape in priority order: overlay, range, then `false` = leave the shelter
   * (the caller's cue).
   */
  onEscape(): boolean {
    if (this.stationOpen !== null || this.gymOpen) {
      this.closeOverlay();
      return true;
    }
    if (this.o.range.active) {
      this.o.range.stop();
      return true;
    }
    return false;
  }

  /** Takes down whichever overlay is up. Idempotent, and the only place the
   *  two store fields are cleared. */
  closeOverlay(): void {
    if (this.stationOpen === null && !this.gymOpen) return;
    this.stationOpen = null;
    this.gymOpen = false;
    setHideoutStation(null);
    setHideoutGym(false);
    this.o.onOverlayChanged(false);
  }

  /**
   * One frame in the cellar. `feet`/`eyeHeight` are the RENDERED pair, as
   * `Interact.update` takes them.
   */
  update(nowMs: number, feet: Readonly<Vec3>, eyeHeight: number): void {
    if (!this.on) return;
    this.maybeRebuild(nowMs);
    const layout = this.layout;
    if (layout === null) return;

    // --- An overlay owns the screen: both buffers drained, not banked.
    if (this.stationOpen !== null || this.gymOpen) {
      this.o.input.consumeFirePresses();
      this.o.input.consumeInteractPresses();
      setInteractPrompt(null);
      return;
    }

    // --- At the firing point ----------------------------------------------
    if (this.o.range.active) {
      // Walking out of the bay puts the gun away; the box is the authority.
      if (distanceToBox(feet, layout.firingBox) > RANGE_LEAVE_SLACK_M) {
        this.o.range.stop();
        // The trigger goes with the gun — a press left in the buffer would go
        // off in a raid.
        this.o.input.consumeFirePresses();
        setInteractPrompt(null);
        return;
      }
      if (this.o.input.consumeInteractPresses() > 0) this.o.range.onInteract(nowMs);
      this.o.range.update(nowMs);
      setInteractPrompt(
        this.drillAvailable
          ? "F — start drill · Esc — leave the range"
          : "Esc — leave the range",
      );
      return;
    }

    // --- Standing in the room, empty-handed: drained rather than ignored.
    this.o.input.consumeFirePresses();

    this.eye.x = feet.x;
    this.eye.y = feet.y + eyeHeight;
    this.eye.z = feet.z;
    viewDirection(this.o.input.yaw, this.o.input.pitch, this.dir);

    let best: HideoutStationSlot | null = null;
    let bestPrompt: string | null = null;
    let bestDist = Infinity;
    const stations = layout.stations;
    const rangeBuilt = (layout.levels[RANGE_MODULE_ID] ?? 0) >= 1;
    for (let i = 0; i < stations.length; i++) {
      const station = stations[i];
      if (station === undefined) continue;
      const dist = distanceToBox(feet, station.interactBox);
      // The BUILT range is the one station you stand IN: its interact box is
      // the
      // firing box, and range mode ends when the feet leave it.
      const reach = rangeBuilt && station.moduleId === RANGE_MODULE_ID ? 0 : STATION_REACH_M;
      if (dist > reach || dist >= bestDist) continue;
      // The reach test is cheap and rejects almost everything before the ray.
      if (!rayHitsBox(this.eye, this.dir, station.interactBox)) continue;
      best = station;
      bestPrompt = this.prompts[i] ?? null;
      bestDist = dist;
    }

    setInteractPrompt(bestPrompt);

    if (this.o.input.consumeInteractPresses() > 0 && best !== null) {
      this.act(best.moduleId, layout);
    }
  }

  // -----------------------------------------------------------------------

  /** F on a station. */
  private act(moduleId: string, layout: HideoutLayout): void {
    const level = layout.levels[moduleId] ?? 0;

    if (level >= 1 && moduleId === RANGE_MODULE_ID) {
      this.o.range.start(
        this.rangeWeapon(),
        layout.targets,
        this.propsGlows,
        this.drillAvailable,
      );
      return;
    }
    if (level >= 1 && moduleId === GYM_MODULE_ID) {
      this.gymOpen = true;
      setHideoutGym(true);
      this.o.onOverlayChanged(true);
      return;
    }
    // Everything else, rubble included, is the menu tab's panel — the build
    // button is IN it.
    this.stationOpen = moduleId;
    setHideoutStation(moduleId);
    this.o.onOverlayChanged(true);
  }

  /**
   * The gun the player practises with: their own, or a loaner — a FACTORY
   * carbine via `mintFactoryMods`, so selling your last rifle does not lock the
   * range.
   */
  private rangeWeapon(): RangeWeapon {
    const profile = this.o.profile();
    const picked = profile === null ? null : pickRangeWeapon(profile.kit);
    if (picked !== null) {
      return { def: picked.def, item: picked.item, name: picked.def.name };
    }
    const mint = (): string => `range:${++this.iidSeq}`;
    const item: ItemInstance = { iid: mint(), defId: ITEM_CARBINE.id, stack: 1 };
    const mods = mintFactoryMods(ITEM_CARBINE, mint);
    if (mods !== undefined) item.mods = mods;
    return { def: ITEM_CARBINE, item, name: ITEM_CARBINE.name };
  }

  /** The projected hideout: what the server would say the cellar looks like
   *  right now, from what it said when the profile was sent. */
  private project(profile: ProfileWire): HideoutState {
    return projectHideout(profile.hideout, this.o.profileAtMs(), performance.now());
  }

  /**
   * Notices the shelter changed shape and rebuilds: a new profile object is
   * caught by reference next frame; a once-a-second recheck covers fuel running
   * out.
   */
  private maybeRebuild(nowMs: number): void {
    const profile = this.o.profile();
    if (profile === null) return;
    const fresh = profile !== this.lastProfile;
    if (!fresh && nowMs < this.nextCheckAtMs) return;
    this.lastProfile = profile;
    this.nextCheckAtMs = nowMs + RECHECK_INTERVAL_MS;

    const h = this.project(profile);
    if (hideoutLayoutKey(h) === this.key && isGeneratorRunning(h) === this.generatorRunning) {
      return;
    }
    // The plates the range is shooting at belong to the layout being thrown
    // away, so the gun goes down with it. Restarting is one F press.
    this.o.range.stop();
    this.install(h, false);
  }

  /**
   * Builds the world and puts it on screen. `teleport` puts a new arrival at
   * the stairs; a rebuild leaves the player where they stand.
   */
  private install(h: HideoutState, teleport: boolean): void {
    const layout = buildHideoutLayout(h);
    const world = buildMapCollisionWorld(layout.map);

    // BEFORE `loadMap`, which is what bakes the preset into the sky and the
    // lights — see the module header.
    this.o.view.setEnv(SHELTER_ENV);
    this.o.view.loadMap(layout.map, { indoor: true });

    const generatorRunning = isGeneratorRunning(h);
    const props = buildHideoutProps(layout, { generatorRunning });
    // Idempotent: the disposer is handed to the view AND called from teardown,
    // and a double dispose is a crash in three.js.
    let disposed = false;
    this.o.view.attachToMap(props.group, () => {
      if (disposed) return;
      disposed = true;
      props.dispose();
    });
    this.propsGlows = props.targetGlows;

    if (teleport) {
      this.o.prediction.respawn(world, layout.spawn);
      this.o.input.yaw = layout.spawnYaw;
      this.o.input.pitch = 0;
    } else {
      this.o.prediction.setWorld(world);
    }

    this.layout = layout;
    this.world = world;
    // The layout's own key, so the installed key describes the installed
    // geometry.
    this.key = layout.key;
    this.generatorRunning = generatorRunning;
    this.drillAvailable = (layout.levels[RANGE_MODULE_ID] ?? 0) >= 2;
    this.prompts = layout.stations.map((s) =>
      stationPrompt(s.moduleId, layout.levels[s.moduleId] ?? 0),
    );

    this.o.tools?.setMap(layout.map);
    // There is a world to walk in, so the click-to-enter gate can go.
    this.o.input.setMapReady(true);
    this.o.onWorldChanged(world);
  }
}

// ---------------------------------------------------------------------------

/**
 * What the crosshair says on a station: unbuilt modules read as WORK ("Clear
 * out"), the two modules that are used get a verb, everything else is named.
 * An unknown id falls back to the raw id — an ugly prompt is a bug report.
 */
function stationPrompt(moduleId: string, level: number): string {
  const name = getHideoutModule(moduleId)?.name ?? moduleId;
  if (level <= 0) return `Clear out — ${name}`;
  if (moduleId === RANGE_MODULE_ID) return "Practise at the range";
  if (moduleId === GYM_MODULE_ID) return "Train";
  return name;
}
